import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Check, X, Eye, Loader2 } from "lucide-react";
import { toast } from "sonner";

export function AdminPageHeader({ eyebrow, title, subtitle, right }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
      <div>
        <p className="label-eyebrow text-xs font-mono uppercase tracking-[0.2em] text-cyan-400">{eyebrow}</p>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white mt-1">{title}</h1>
        {subtitle && <p className="text-sm text-slate-400 mt-1">{subtitle}</p>}
      </div>
      {right}
    </div>
  );
}

export function ApproveButton({ onConfirm, label = "Approve", testId }) {
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const run = async () => { setLoading(true); try { await onConfirm(); setOpen(false); } finally { setLoading(false); } };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid={testId} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-sm hover:bg-emerald-500/25"><Check className="w-4 h-4" /> {label}</button>
      </DialogTrigger>
      <DialogContent className="bg-[#0A0F1D] border-cyan-500/20 text-slate-200">
        <DialogHeader><DialogTitle className="text-white">Confirm {label}</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-400">Are you sure you want to {label.toLowerCase()} this request? This action updates balances and cannot be undone.</p>
        <DialogFooter>
          <button onClick={() => setOpen(false)} className="px-4 py-2 rounded-lg text-sm text-slate-300 hover:bg-white/5">Cancel</button>
          <button onClick={run} disabled={loading} data-testid={`${testId}-confirm`} className="btn-glow px-4 py-2 rounded-lg text-sm font-semibold text-slate-950 inline-flex items-center gap-2">{loading && <Loader2 className="w-4 h-4 animate-spin" />} Confirm</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function RejectButton({ onConfirm, testId }) {
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const run = async () => {
    if (!reason.trim()) return toast.error("Rejection reason is required");
    setLoading(true); try { await onConfirm(reason); setOpen(false); setReason(""); } finally { setLoading(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid={testId} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-500/15 border border-red-500/30 text-red-300 text-sm hover:bg-red-500/25"><X className="w-4 h-4" /> Reject</button>
      </DialogTrigger>
      <DialogContent className="bg-[#0A0F1D] border-cyan-500/20 text-slate-200">
        <DialogHeader><DialogTitle className="text-white">Reject Request</DialogTitle></DialogHeader>
        <textarea data-testid={`${testId}-reason`} value={reason} onChange={(e) => setReason(e.target.value)} rows={3} placeholder="Enter rejection reason (required)"
          className="w-full bg-slate-900/60 border border-cyan-500/20 rounded-lg px-4 py-2.5 text-slate-100 focus:border-cyan-400 focus:outline-none resize-none" />
        <DialogFooter>
          <button onClick={() => setOpen(false)} className="px-4 py-2 rounded-lg text-sm text-slate-300 hover:bg-white/5">Cancel</button>
          <button onClick={run} disabled={loading} data-testid={`${testId}-confirm`} className="px-4 py-2 rounded-lg text-sm font-semibold bg-red-500 text-white inline-flex items-center gap-2 hover:bg-red-600">{loading && <Loader2 className="w-4 h-4 animate-spin" />} Reject</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ImageViewer({ src, testId, label = "View" }) {
  if (!src) return <span className="text-xs text-slate-600">None</span>;
  return (
    <Dialog>
      <DialogTrigger asChild>
        <button data-testid={testId} className="inline-flex items-center gap-1 text-cyan-400 text-sm hover:underline"><Eye className="w-4 h-4" /> {label}</button>
      </DialogTrigger>
      <DialogContent className="bg-[#0A0F1D] border-cyan-500/20 max-w-lg">
        <DialogHeader><DialogTitle className="text-white">Document</DialogTitle></DialogHeader>
        <img src={src} alt="Document" className="w-full rounded-lg" />
      </DialogContent>
    </Dialog>
  );
}

export function DateFilter({ value, onChange }) {
  const opts = [["today", "Today"], ["7days", "7 Days"], ["30days", "30 Days"], ["all", "All Time"]];
  return (
    <div className="flex gap-2 flex-wrap">
      {opts.map(([v, l]) => (
        <button key={v} data-testid={`date-filter-${v}`} onClick={() => onChange(v)}
          className={`px-4 py-2 rounded-lg text-sm border transition-colors ${value === v ? "bg-cyan-500/15 border-cyan-500/40 text-cyan-300" : "border-white/10 text-slate-400 hover:border-cyan-500/20"}`}>{l}</button>
      ))}
    </div>
  );
}
